import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { SessionService } from '@app/shared/helpers';
import { MFAService } from '@app/core/http-services/mfa.service';
import { ToastService } from '@ceslicensing/toast-notification';
import { environment } from '@env/environment';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CountryISO, SearchCountryField, PhoneNumberFormat } from 'ngx-intl-tel-input';
const MFA_OTP_GRANT_TYPE: string = 'http://auth0.com/oauth/grant-type/mfa-otp';
const MFA_OOB_GRANT_TYPE: string = 'http://auth0.com/oauth/grant-type/mfa-oob';
@Component({
  selector: 'app-authenticator-setup',
  templateUrl: './authenticator-setup.component.html',
  styleUrls: ['./authenticator-setup.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AuthenticatorSetupComponent implements OnInit, OnDestroy {
  destroy$: Subject<boolean> = new Subject<boolean>();
  @Input() appDetails: any;
  loadingConfig: any = { loading: false, error: false, msg: '', errorObj: {} };

  preferredCountries: CountryISO[] = [
    CountryISO.UnitedStates,
    CountryISO.UnitedKingdom,
    CountryISO.India,
  ];
  selectedCountryISO: CountryISO = CountryISO.UnitedStates;
  SearchCountryField = SearchCountryField;
  PhoneNumberFormat = PhoneNumberFormat;
  password: any;
  verifyPassword: boolean = false;
  loader: boolean = false;
  title: string;
  barcodeUri: any;
  qrOtp: any;
  authenticatorMode: string;
  oobCode: any;
  phoneNumber: any;
  smsStep1: boolean = true;
  smsStep2: boolean = false;
  emailStep1: boolean = true;
  emailStep2: boolean = false;
  userSMSOtp: any;
  userEmailOtp: any;
  userEmail: any;
  phoneError: boolean = false;
  phoneErrorMsg: string;
  constructor(
    public dialog: DialogRef,
    private mfaService: MFAService,
    private sessionService: SessionService,
    private toast: ToastService
  ) {}
  ngOnInit(): void {
    this.changeTitle();
    this.authenticatorMode = this.appDetails.mode;
    this.switchToAuthMode();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
  /**
   * On cancel
   */
  onCancel() {
    this.close(false);
  }

  /**
   * Close confirmation modal with response yes/no
   */
  close(isSuccess) {
    let obj: any = {
      isSuccess,
    };
    this.dialog.close(obj);
  }

  /**
   * Update the modal title based on authenticator mode
   */
  changeTitle() {
    if (this.verifyPassword) {
      this.title = 'Verify Your Password';
    } else if (!this.verifyPassword && this.authenticatorMode == 'otp') {
      this.title = 'Set Up Authenticator App';
    } else if (!this.verifyPassword && this.authenticatorMode == 'push-notification') {
      this.title = 'Set Up Push Notification App';
    } else if (!this.verifyPassword && this.authenticatorMode == 'sms') {
      this.title = 'Set Up SMS';
    } else if (!this.verifyPassword && this.authenticatorMode == 'email') {
      this.title = 'Set Up Email';
    }
  }
  /**
   * Submit your password to authenticate user
   */
  confirmPwd() {
    let body: any = {
      grant_type: 'password',
      username: this.appDetails.email,
      password: this.password,
      client_id: environment.mfaAppClientId,
      client_secret: environment.mfaAppClientSecret,
      audience: `https://${environment.auth.auth_domain}/mfa/`,
    };
    this.loader = true;
    this.mfaService
      .getAuthTokenApi(body)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          this.loader = false;
          if (response.access_token) {
            this.sessionService.setMFAToken(response.access_token);
            this.switchToAuthMode();
          }
        },
        (err) => {
          this.loader = false;
          if (err?.error?.error_description) {
            this.toast.error(err.error.error_description);
          } else {
            this.toast.error('Failed to authenticate user password, Please try again later.');
          }
        }
      );
  }

  /**
   * Enroll google authenticator OTP
   */
  getAuthenticatorEnroll() {
    this.loader = true;
    let body: any = {
      authenticator_types: ['otp'],
    };
    this.mfaService
      .enrollAuthenticatorApi(body)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          if (response) {
            this.verifyPassword = false;
            this.barcodeUri = response.barcode_uri;
            this.changeTitle();
          }
          this.loader = false;
        },
        () => {
          this.loader = false;
          this.toast.error('Something went wrong, Please try again later.');
          this.close(false);
        }
      );
  }

  /**
   * Submit QR Code OTP
   */
  submitQrCode() {
    this.loader = true;
    let body: any = {
      grant_type: MFA_OTP_GRANT_TYPE,
      mfa_token: this.sessionService.getCustomMfaToken(),
      client_id: environment.mfaAppClientId,
      client_secret: environment.mfaAppClientSecret,
    };
    if (this.authenticatorMode == 'otp') {
      body['grant_type'] = MFA_OTP_GRANT_TYPE;
      body['otp'] = `${this.qrOtp}`;
    } else if (this.authenticatorMode == 'push-notification') {
      body['grant_type'] = MFA_OOB_GRANT_TYPE;
      body['oob_code'] = `${this.oobCode}`;
    }
    this.mfaService
      .getAuthTokenApi(body)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          if (response) {
            this.verifyPassword = false;

            if (this.authenticatorMode == 'otp') {
              this.toast.success('OTP Authenticator successfully enrolled.');
            } else if (this.authenticatorMode == 'push-notification') {
              this.toast.success('Guardian push notification successfully enrolled.');
            }
            this.close(true);
          }
          this.loader = false;
        },
        (err) => {
          this.close(true);
          if (err.error.status == 403) {
            this.toast.error('Timeout for setting MFA, Please try again');
          } else if (err?.error?.error_description) {
            this.toast.error(err.error.error_description);
          } else {
            this.toast.error('Failed to enroll OTP Authenticator, Please try again later.');
          }
          this.loader = false;
        }
      );
  }

  /**
   * Get push authenticator barcode URI
   */
  getPushAuthenticator() {
    this.loader = true;
    let body: any = {
      authenticator_types: ['oob'],
      oob_channels: ['auth0'],
    };
    this.mfaService
      .enrollAuthenticatorApi(body)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          if (response) {
            this.verifyPassword = false;
            this.barcodeUri = response.barcode_uri;
            this.oobCode = response.oob_code;
            this.changeTitle();
          }
          this.loader = false;
        },
        () => {
          this.loader = false;
          this.toast.error('Something went wrong, Please try again later.');
          this.close(false);
        }
      );
  }

  /**
   * Switch to auth mode and  change title
   */
  switchToAuthMode() {
    switch (this.authenticatorMode) {
      case 'otp':
        this.getAuthenticatorEnroll();
        break;
      case 'push-notification':
        this.getPushAuthenticator();
        break;
      case 'sms':
      case 'email':
        this.verifyPassword = false;
        this.changeTitle();
        break;
    }
  }

  /**
   * Handle phone number errors
   * @param error
   */
  onContactNumberError(error) {
    error.error = error?.error?.replace('Phone', 'Contact');
    if (error.isError) {
      this.phoneError = error.isError;
      this.phoneErrorMsg = error.error;
    } else {
      this.phoneError = error.isError;
      this.phoneErrorMsg = '';
    }
  }

  /**
   * Submit phone number to get OTP on phone
   */
  submitPhoneNumber() {
    this.loader = true;
    let body: any = {
      authenticator_types: ['oob'],
      oob_channels: ['sms'],
      phone_number: this.phoneNumber.e164Number,
    };
    this.mfaService
      .enrollAuthenticatorApi(body)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          if (response) {
            this.smsStep1 = false;
            this.smsStep2 = true;
            this.verifyPassword = false;
            this.oobCode = response.oob_code;
            this.changeTitle();
          }
          this.loader = false;
        },
        (err) => {
          this.loader = false;
          if (err?.error?.error_description) {
            this.toast.error(err.error.error_description);
          } else {
            this.toast.error('Something went wrong, Please try again later.');
          }
        }
      );
  }
  /**
   * Submit Email to get OTP on email
   */
  submitEmailEnroll() {
    this.loader = true;
    let body: any = {
      authenticator_types: ['oob'],
      oob_channels: ['email'],
      email: this.userEmail,
    };
    this.mfaService
      .enrollAuthenticatorApi(body)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          if (response) {
            this.emailStep1 = false;
            this.emailStep2 = true;
            this.verifyPassword = false;
            this.oobCode = response.oob_code;
            this.changeTitle();
          }
          this.loader = false;
        },
        (err) => {
          this.loader = false;
          if (err?.error?.error_description) {
            this.toast.error(err.error.error_description);
          } else {
            this.toast.error('Something went wrong, Please try again later.');
          }
        }
      );
  }

  /**
   * Submit SMS otp, to enroll SMS MFA
   */
  submitSMSOtp() {
    let body: any = {
      client_id: environment.mfaAppClientId,
      client_secret: environment.mfaAppClientSecret,
      grant_type: MFA_OOB_GRANT_TYPE,
      mfa_token: this.sessionService.getCustomMfaToken(),
      oob_code: this.oobCode,
      binding_code: `${this.userSMSOtp}`,
    };
    this.loader = true;
    this.mfaService
      .getAuthTokenApi(body)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.loader = false;
          this.toast.success('SMS MFA successfully enrolled.');
          this.close(true);
        },
        (err) => {
          this.close(true);
          this.loader = false;
          if (err.error.status == 403) {
            this.toast.error('Timeout for setting MFA, Please try again');
          } else if (err?.error?.error_description) {
            this.toast.error(err.error.error_description);
          } else {
            this.toast.error('Failed to enroll SMS MFA, Please try again later.');
          }
        }
      );
  }
  /**
   * Submit email otp to enroll Email mfa
   */
  submitEmailOtp() {
    let body: any = {
      client_id: environment.mfaAppClientId,
      client_secret: environment.mfaAppClientSecret,
      grant_type: MFA_OOB_GRANT_TYPE,
      mfa_token: this.sessionService.getCustomMfaToken(),
      oob_code: this.oobCode,
      binding_code: `${this.userEmailOtp}`,
    };
    this.loader = true;
    this.mfaService
      .getAuthTokenApi(body)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.loader = false;
          this.toast.success('Email MFA successfully enrolled.');
          this.close(true);
        },
        (err) => {
          this.loader = false;
          this.close(true);
          if (err?.error?.status == 403) {
            this.toast.error('Timeout for setting MFA, Please try again');
          } else if (err?.error?.error_description) {
            this.toast.error(err.error.error_description);
          } else {
            this.toast.error('Failed to enroll Email MFA, Please try again later.');
          }
        }
      );
  }

  /**
   * On authenticator otp change
   */
  onOtpChange(event) {
    this.qrOtp = event;
  }

  /**
   * Set value of sms otp change
   */
  onSMSOtpChange(event) {
    this.userSMSOtp = event;
  }

  /**
   * Set value of email otp change
   */
  onEmailOtpChange(event) {
    this.userEmailOtp = event;
  }
}
